import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { Section } from '@product/Section/Section';
import { TechnicalTable } from '@product/TechnicalTable/TechnicalTable';
import { useTranslation } from 'next-i18next';
import { TechnicalDiagram } from '@product/TechnicalDiagram/TechnicalDiagram';
import { ChemicalSearch } from '../ChemicalSearch';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { ProductAttribute, ProductAttributeUnion } from '@libs/attribute.helper';
import { useTechnicalDataQuery } from './useTechnicalDataQuery';
import { getLocale } from '@libs/constants';
import { QueryStatusHandler } from '@common/QueryStatusHandler/QueryStatusHandler';
import { ProductHelper } from '@models/graphql';
import { CategoryHelper } from '@libs/category.helper';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';

type ChemicalProductAttributes = {
  [key: string]: ProductAttribute<ProductAttributeUnion>;
};

export const TechnicalDataTab = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const { t } = useTranslation('product/productDetails');
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const primaryCategoryCode = useProductPageStore(state => state.primaryCategoryCode);
  const { data, status } = useTechnicalDataQuery(locale);

  const sortedProductDiagrams = useMemo(() => {
    if (!currentProduct.assets) {
      return [];
    }

    const currentProductWithAdditionalAttributes = {
      ...currentProduct,
      attributes: {
        ...currentProduct.attributes,
        ...data?.technicalProductAttributes,
      },
    };
    const { technicalMedia } = ProductHelper.getAssetsMedia(currentProductWithAdditionalAttributes);
    return technicalMedia?.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)) ?? [];
  }, [currentProduct, data]);

  return (
    <QueryStatusHandler status={status}>
      <div className="text-sm pt-0 lg:pt-8">
        {data?.categories
          ?.filter(({ attributes }) => attributes?.some(attr => !!attr.value))
          .map(({ name, label, attributes }) => (
            <Section key={name} title={t(label ?? '')}>
              <TechnicalTable data={attributes} />
            </Section>
          ))}
        {sortedProductDiagrams.length > 0 && (
          <Section title={t('PRODUCT_DETAILS.TECHNICAL_DATA_ADDITIONAL_INFO_AND_DIAGRAMS_TITLE')}>
            {sortedProductDiagrams.map(diagram => (
              <TechnicalDiagram diagram={diagram} key={diagram?.sort ?? 0} />
            ))}
          </Section>
        )}
        {data?.chemicalAttributeNames && data?.chemicalAttributeNames.length > 1 && (
          <ChemicalSearch
            chemicalAttributes={data.chemicalAttributeNames}
            productAttributes={data?.technicalProductAttributes as ChemicalProductAttributes}
          />
        )}
        {CategoryHelper.isEmotor(primaryCategoryCode) && (
          <p className="flex">
            <Icon id={IconID.INFO} size={6} color={IconColor.BLUE} />
            <span className="ml-2 -mt-1">{t('PRODUCT_DETAILS.MISSING_TECHNICAL_DATA_HINT')}</span>
          </p>
        )}
      </div>
    </QueryStatusHandler>
  );
};
